import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  ActionsBanner,
  Avatar,
  ColumnsButton,
  ColumnsDrawer,
  CompanyFilter,
  CreateButton,
  Currency,
  CurrencyInput,
  DateTime,
  ExportDialog,
  ExportDropdown,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  MemberContactPopover,
  MemberFilter,
  MultiCurrency,
  OpportunityLink,
  OpportunityStageCategoryFilter,
  OpportunityStageFilter,
  OpportunityTypeFilter,
  Page,
  Percent,
  PracticeFilter,
  SearchInput,
  Tag,
  Tooltip,
  YesNoFilter,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import {
  FilterButton,
  FiltersDrawer,
  FiltersGroup,
  OpportunityFiltersBar,
  OpportunityPipelineFilter,
  OpportunityTagFilter,
  ReportPeriodFilter,
} from '~/components/filters';
import { useApi, useConfirmation, useIntegrations, useToast, useWorkspace } from '~/contexts';
import {
  useAuth,
  useDocumentTitle,
  useFeatures,
  useIsMounted,
  useOpportunityFilters,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
  useViewColumns,
} from '~/hooks';
import opportunityStageCategories from '~/lookups/opportunity-stage-categories';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort, mimeTypes } from '~/utils';
import List from '../../reports/components/List';
import ListTooltip from '../../reports/components/ListTooltip';
import useReportsSearchParamsConfig from '../../reports/hooks/useReportsSearchParamsConfig.js';
import HubspotIndicator from '../components/HubspotIndicator';
import SalesforceIndicator from '../components/SalesforceIndicator';
import OpportunitiesSummaryWidget from './OpportunitiesSummaryWidget';
import OpportunityCloneForm from './OpportunityCloneForm';
import OpportunityDeleteConfirmation from './OpportunityDeleteConfirmation';
import OpportunityForm from './OpportunityForm';
import OpportunityToProjectForm from './OpportunityToProjectForm';
import ProbabilityBar from './ProbabilityBar';
import CreateClientAndProjectDialog from './CreateClientAndProjectDialog';
import { BulkDelete, BulkSetPriority, BulkSetStage } from './bulk';

const SummarySection = styled.section`
  background: ${colors.grey5};
  padding: 1rem;
  margin: 0 -1rem;
  overflow: auto;
  scrollbar-width: none;
`;

const InfoContainer = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const StageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const OpportunityName = styled.div`
  display: flex;
  align-items: center;
`;

const Small = styled.small`
  display: block;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Title = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

const Owner = styled.div`
  display: flex;
  align-items: center;
`;

const OwnerInfo = styled.span`
  margin-left: 0.5rem;
`;

function FirstAssignment({ tags }) {
  const firstTag = tags[0];
  if (!firstTag) return null;

  return (
    <Tag style={{ backgroundColor: colors.grey5 }}>
      <small>{firstTag.name}</small>
    </Tag>
  );
}

function Assignments({ tags }) {
  const count = tags.length - 1;
  if (count <= 0) return null;

  return (
    <Tooltip
      message={
        <div style={{ fontSize: '1rem' }}>
          <Title>Tags</Title>
          {tags.map((tag) => (
            <Tag style={{ backgroundColor: colors.grey5 }} key={tag.id}>
              <small>{tag.name}</small>
            </Tag>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
        <small>+{count}</small>
      </Tag>
    </Tooltip>
  );
}

function FirstProject({ projects }) {
  const firstProject = projects[0];
  if (!firstProject) return null;

  return (
    <Tag style={{ backgroundColor: colors.grey5 }}>
      <small>{firstProject.name}</small>
    </Tag>
  );
}

function Projects({ projects }) {
  const count = projects.length - 1;
  if (count <= 0) return null;

  return (
    <Tooltip
      message={
        <div style={{ fontSize: '1rem' }}>
          <Title>Projects</Title>
          {projects.map((project) => (
            <Tag style={{ backgroundColor: colors.grey5 }} key={project.id}>
              <small>{project.name}</small>
            </Tag>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
        <small>+{count}</small>
      </Tag>
    </Tooltip>
  );
}

export default function OpportunitiesListPage({ renderFilters, mode = 'page', companyId, sessionKey }) {
  const integrations = useIntegrations();

  const documentTitle = useDocumentTitle();
  useEffect(() => {
    if (mode === 'page') documentTitle.set('Opportunities');
  }, [mode, documentTitle]);

  const { workspace } = useWorkspace();
  const features = useFeatures();
  const api = useApi();
  const auth = useAuth();
  const history = useHistory();
  const { opportunityId } = useParams();
  const toast = useToast();
  const confirmation = useConfirmation();
  const isMounted = useIsMounted();
  const location = useLocation();

  const [cloneTarget, setCloneTarget] = useState(null);
  const [opportunityToProject, setOpportunityToProject] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const searchParamsConfig = useSearchParamsConfig();
  const opportunityFilters = useOpportunityFilters({
    opportunityStageCategories: {
      default: [opportunityStageCategories.open],
      ...searchParamsConfig.opportunityStageCategories,
    },
  });

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      q: '',
      companies: [],
      opportunityOpenedPeriod: null,
      opportunityClosedPeriod: null,
      opportunityPipelines: null,
      page: 0,
      size: 25,
      sort: new QuerySort('convertedWeightedAmount', 'desc'),
      ...opportunityFilters.filters,
    },
    action: 'load',
  });

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const loadMore = useCallback(() => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      ) {
        return state;
      }

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  }, []);

  const removeItem = (id) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.filter((i) => i.id !== id),
        total: state.data.total - 1,
      },
    }));
  };

  const updateItem = (item) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.map((i) => (i.id === item.id ? { ...i, ...item } : i)),
      },
    }));
  };

  const { action, data, isReady, params, searchParamsStatus } = query;

  const selection = useRowSelection({ rows: data?.results });
  const selectedOpportunityPipelineIds = useMemo(() => {
    const selectedIds = new Set(selection.ids);
    return _.sortBy(
      _.uniq(
        (data?.results ?? [])
          .filter((row) => selectedIds.has(row.id))
          .map((row) => row.opportunityPipelineId)
          .filter(Boolean),
      ),
    );
  }, [data?.results, selection.ids]);

  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        companies: searchParamsConfig.companies,
        sort: { default: new QuerySort('name', 'asc'), ...searchParamsConfig.sort },
        opportunityOpenedPeriod: reportsSearchParamsConfig.opportunityOpenedPeriod,
        opportunityClosedPeriod: reportsSearchParamsConfig.opportunityClosedPeriod,
        opportunityPipelines: searchParamsConfig.opportunityPipelines,
        ...opportunityFilters.searchParamsConfig,
      }),
      [reportsSearchParamsConfig, searchParamsConfig, opportunityFilters.searchParamsConfig],
    ),
    sessionKey,
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      q: params.q,
      page: params.page,
      size: params.size,
      sort: params.sort,
      companyId: params.companies.length ? params.companies?.map((v) => v.id) : companyId,
      opportunityOpenedStart: params.opportunityOpenedPeriod?.start ?? undefined,
      opportunityOpenedEnd: params.opportunityOpenedPeriod?.end ?? undefined,
      opportunityClosedStart: params.opportunityClosedPeriod?.start ?? undefined,
      opportunityClosedEnd: params.opportunityClosedPeriod?.end ?? undefined,
      opportunityPipelineId: params.opportunityPipelines?.map((pipeline) => pipeline.id),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...opportunityFilters.mapUrlSearchParams(params),
    }),
    [params, companyId, opportunityFilters],
  );

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).opportunities().get(urlSearchParams);

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        action: null,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch (error) {
      setQuery((state) => ({ ...state, data: { total: 0, results: [] } }));
      toast.error(error.message);
    }
  }, [workspace.id, api, isMounted, urlSearchParams, toast]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);

  async function handleSaved(opportunity) {
    const { data } = await api.www.workspaces(workspace.id).opportunities().get({ ids: opportunity.id });
    updateItem(data[0]);
    refreshSummary();
  }

  const handleWon = async (opportunity) => {
    await handleSaved(opportunity); // Saving the opportunity first in case the confirmation only closes the dialog without saving
    confirmation.prompt((resolve) => (
      <CreateClientAndProjectDialog opportunity={opportunity} onClose={resolve} onSaved={handleSaved} />
    ));
  };

  const handleFilter = (values) => {
    if (values !== params) {
      selection.clear();
    }
    setParams({ ...values });
    searchParams.set({ ..._.omit(values, 'sort') });
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
    selection.clear();
  };

  async function handleDeleted(opportunity) {
    removeItem(opportunity.id);
  }

  const handleDeleteConfirmation = (opportunity) => {
    confirmation.prompt((resolve) => (
      <OpportunityDeleteConfirmation
        opportunity={opportunity}
        onClose={resolve}
        onDelete={() => {
          handleDeleted(opportunity);
          refreshSummary();
          resolve(true);
        }}
      />
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          refreshSummary();
          setParams({ page: 0 });
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkSetStage = async () => {
    await confirmation.prompt((resolve) => (
      <BulkSetStage
        ids={selection.ids}
        opportunityPipelineIds={selectedOpportunityPipelineIds}
        onComplete={async () => {
          selection.clear();
          refreshSummary();
          setParams({ page: 0 });
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkSetPriority = async () => {
    await confirmation.prompt((resolve) => (
      <BulkSetPriority
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          refreshSummary();
          setParams({ page: 0 });
        }}
        onClose={resolve}
      />
    ));
  };

  const handleEdit = ({ id }) => {
    history.push({
      pathname: `/app/${workspace.key}/pipeline/opportunities/edit/${id}`,
      search: location.search,
      state: { scrollToTop: false },
    });
  };

  const handleOverview = ({ id }) => {
    history.push(`/app/${workspace.key}/pipeline/opportunities/${id}/overview`);
  };

  const refreshSummary = () => {
    setRefreshKey((refreshKey) => refreshKey + 1);
  };

  async function handleCloned(opportunity) {
    try {
      toast.success('Opportunity cloned successfully.');
      history.push({
        pathname: `/app/${workspace.key}/pipeline/opportunities/${opportunity.id}/overview/edit`,
        search: location.search,
        state: { scrollToTop: false },
      });
    } catch ({ message }) {
      toast.error(message);
    }
  }

  function handleClose() {
    history.push({
      pathname: `/app/${workspace.key}/pipeline/opportunities`,
      search: location.search,
      state: { scrollToTop: false },
    });
    documentTitle.set('Opportunities');
  }

  const handleExport = async (filename, mimeType) => {
    await confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .opportunities()
          .export(
            {
              ...urlSearchParams,
              size: null,
            },
            {
              headers: { accept: mimeType },
              responseType: 'blob',
            },
          )}
        onClose={resolve}
      />
    ));
  };

  const [dialog, setDialog] = useState(null);

  const columns = useViewColumns({
    storageKey: 'opportunities_list_columns',
    columns: _.compact([
      { key: 'name', name: 'Name', disabled: true },
      { key: 'owner', name: 'Owner', default: true },
      { key: 'opportunityPipeline', name: 'Pipeline', default: false },
      { key: 'stage', name: 'Stage', default: true },
      { key: 'projects', name: 'Projects', default: true },
      { key: 'tags', name: 'Tags', default: true },
      { key: 'amount', name: 'Amount', default: true },
      { key: 'probability', name: 'Probability', default: true },
      features.practices && { key: 'practice', name: 'Practice', default: false },
      { key: 'priority', name: 'Priority', default: false },
      { key: 'opportunityType', name: 'Type', default: false },
      { key: 'opportunityLeadSource', name: 'Lead Source', default: false },
      { key: 'lastActivity', name: 'Last Activity', default: true },
      { key: 'openDate', name: 'Opened', default: true },
      { key: 'closeDate', name: 'Closing', default: true },
    ]),
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  const Container = useCallback(
    (props) => (mode === 'page' ? <Page {...props} /> : <React.Fragment {...props} />),
    [mode],
  );

  if (!isReady && !data) return <PageLoader />;

  const salesforceEnabled = integrations?.salesforce;
  const hubspotEnabled = integrations?.hubspot;
  const hasSelectionColumn = mode === 'page' && auth.pipeline.manage;

  return (
    <Container>
      {mode === 'page' && (
        <>
          <Page.Header>
            <Page.Info>
              <Page.Eyebrow>Pipeline</Page.Eyebrow>
              <Page.Title>Opportunities</Page.Title>
            </Page.Info>

            <Page.Actions>
              <ExportDropdown>
                {({ setIsOpen }) => (
                  <>
                    <ExportDropdown.Item
                      onClick={async () => {
                        await handleExport(`opportunities.csv`, mimeTypes.csv);
                        setIsOpen(false);
                      }}>
                      Export to CSV
                    </ExportDropdown.Item>

                    <ExportDropdown.Item
                      onClick={async () => {
                        await handleExport(`opportunities.xlsx`, mimeTypes.xlsx);
                        setIsOpen(false);
                      }}>
                      Export to Excel
                    </ExportDropdown.Item>
                  </>
                )}
              </ExportDropdown>

              <ColumnsButton isOutline onClick={handleShowColumnsDrawer} />

              <FilterButton isOutline onClick={showFilters} />

              {auth.pipeline.manage && (
                <CreateButton
                  tooltip="New Opportunity"
                  onClick={() => history.push(`/app/${workspace.key}/pipeline/opportunities/new${location.search}`)}
                />
              )}
            </Page.Actions>
          </Page.Header>
        </>
      )}

      {renderFilters ? (
        renderFilters({ params, onFilter: handleFilter })
      ) : (
        <>
          <Page.Filters>
            <FiltersBar>
              {(params.opportunityOpenedPeriod?.start || params.opportunityOpenedPeriod?.end) && (
                <ReportPeriodFilter
                  placeholder="Opened Date Range"
                  value={params.opportunityOpenedPeriod}
                  onChange={({ value }) => handleFilter({ opportunityOpenedPeriod: value })}
                />
              )}

              {(params.opportunityClosedPeriod?.start || params.opportunityClosedPeriod?.end) && (
                <ReportPeriodFilter
                  placeholder="Expected Closed Date Range"
                  value={params.opportunityClosedPeriod}
                  onChange={({ value }) => handleFilter({ opportunityClosedPeriod: value })}
                />
              )}

              <SearchInput
                placeholder="Search"
                value={params.q}
                onChange={({ target: { value } }) => handleFilter({ q: value })}
              />

              <CompanyFilter
                name="companies"
                placeholder="Company"
                value={params.companies}
                onChange={({ target: { value } }) => handleFilter({ companies: value })}
              />

              <OpportunityFiltersBar
                filters={params}
                sticky={[
                  'opportunityPipelines',
                  'opportunitySalesforceSynced',
                  'opportunityHubspotSynced',
                  'opportunityOwners',
                  'opportunityPractices',
                  'opportunityStages',
                  'opportunityStageCategories',
                  'opportunityTags',
                  'opportunityTypes',
                ]}
                onChange={handleFilter}
              />
            </FiltersBar>
          </Page.Filters>

          <Filters values={params} isOpen={filtersVisible} onApply={handleFilter} onClose={hideFilters} />
        </>
      )}

      {mode === 'page' && (
        <Page.Section sticky>
          <SummarySection>
            <OpportunitiesSummaryWidget
              key={refreshKey}
              q={urlSearchParams.q}
              opportunityStageCategoryId={urlSearchParams.opportunityStageCategoryId}
              opportunityStageId={urlSearchParams.opportunityStageId}
              opportunityTagId={urlSearchParams.opportunityTagId}
              opportunityTypeId={urlSearchParams.opportunityTypeId}
              opportunityAmountGreaterThanOrEqual={urlSearchParams.opportunityAmountGreaterThanOrEqual}
              opportunitySalesforceSynced={urlSearchParams.opportunitySalesforceSynced}
              opportunityHubspotSynced={urlSearchParams.opportunityHubspotSynced}
              opportunityOwnerId={urlSearchParams.opportunityOwnerId}
              opportunityPracticeId={urlSearchParams.opportunityPracticeId}
              companyId={urlSearchParams.companyId}
              opportunityClosedStart={urlSearchParams.opportunityClosedStart}
              opportunityClosedEnd={urlSearchParams.opportunityClosedEnd}
              opportunityOpenedStart={urlSearchParams.opportunityOpenedStart}
              opportunityOpenedEnd={urlSearchParams.opportunityOpenedEnd}
              opportunityPipelineId={urlSearchParams.opportunityPipelineId}
            />
          </SummarySection>
        </Page.Section>
      )}

      <Page.ListView>
        <ListView>
          <ListView.Header>
            {hasSelectionColumn && (
              <ListViewSelection.Column
                selection={selection}
                disabled={data.results.length === 0 || action === 'filter'}
              />
            )}
            <ListView.Column sticky minWidth="16rem" name="name" onSort={handleSort} sort={params.sort}>
              Name
            </ListView.Column>

            <ListView.Column
              minWidth="14rem"
              name="owner.name"
              onSort={handleSort}
              sort={params.sort}
              isVisible={columns.visible.owner}>
              Owner
            </ListView.Column>

            <ListView.Column
              width="14rem"
              name="opportunityPipeline.name"
              onSort={handleSort}
              sort={params.sort}
              isVisible={columns.visible.opportunityPipeline}>
              Pipeline
            </ListView.Column>

            <ListView.Column
              width="12rem"
              name="opportunityStage"
              onSort={handleSort}
              sort={params.sort}
              isVisible={columns.visible.stage}>
              Stage
            </ListView.Column>

            <ListView.Column minWidth="16rem" isVisible={columns.visible.projects}>
              Projects
            </ListView.Column>

            <ListView.Column minWidth="10rem" isVisible={columns.visible.tags}>
              Tags
            </ListView.Column>

            <ListView.Column
              width="10rem"
              name="convertedAmount"
              onSort={handleSort}
              sort={params.sort}
              align="right"
              isVisible={columns.visible.amount}>
              Amount
            </ListView.Column>

            <ListView.Column
              width="8rem"
              name="probability"
              onSort={handleSort}
              sort={params.sort}
              align="right"
              isVisible={columns.visible.probability}>
              Probability
            </ListView.Column>

            <ListView.Column
              width="12rem"
              name="practice.name"
              onSort={handleSort}
              sort={params.sort}
              isVisible={features.practices && columns.visible.practice}>
              Practice
            </ListView.Column>

            <ListView.Column
              width="8rem"
              name="priorityId"
              onSort={handleSort}
              sort={params.sort}
              isVisible={columns.visible.priority}>
              Priority
            </ListView.Column>

            <ListView.Column
              width="16rem"
              name="opportunityType.name"
              onSort={handleSort}
              sort={params.sort}
              isVisible={columns.visible.opportunityType}>
              Type
            </ListView.Column>

            <ListView.Column
              width="12rem"
              name="opportunityLeadSource.name"
              onSort={handleSort}
              sort={params.sort}
              isVisible={columns.visible.opportunityLeadSource}>
              Lead Source
            </ListView.Column>

            <ListView.Column
              width="9rem"
              name="lastActivityDate"
              onSort={handleSort}
              sort={params.sort}
              align="right"
              isVisible={columns.visible.lastActivity}>
              Last Activity
            </ListView.Column>
            <ListView.Column
              width="7rem"
              name="openDate"
              onSort={handleSort}
              sort={params.sort}
              align="right"
              isVisible={columns.visible.openDate}>
              Opened
            </ListView.Column>

            <ListView.Column
              width="7rem"
              name="closeDate"
              onSort={handleSort}
              sort={params.sort}
              align="right"
              isVisible={columns.visible.closeDate}>
              Closing
            </ListView.Column>

            {salesforceEnabled && (
              <ListView.Column width="4rem" name="salesforceId" onSort={handleSort} sort={params.sort} align="right">
                <SalesforceIndicator variant="grey" />
              </ListView.Column>
            )}

            {hubspotEnabled && (
              <ListView.Column width="4rem" name="hubspotId" onSort={handleSort} sort={params.sort} align="right">
                <HubspotIndicator variant="grey" />
              </ListView.Column>
            )}

            <ListViewActions.Column isVisible={mode === 'page'} />
          </ListView.Header>

          <ListView.Body fade={action === 'filter'}>
            {query.data.results.map((opportunity) => {
              const {
                id,
                amount,
                opportunityStage,
                opportunityPipeline,
                tags,
                probability,
                openDate,
                closeDate,
                currency,
                convertedAmount,
                permissions,
                owner,
                projects,
                salesforceId,
                hubspotId,
                lastActivityDate,
                opportunityLeadSource,
                opportunityType,
                priorityId,
                practice,
              } = opportunity;

              async function handleRowClick(opportunity) {
                handleOverview(opportunity);
              }

              return (
                <ListView.Row key={id} onClick={() => handleRowClick(opportunity)}>
                  {hasSelectionColumn && <ListViewSelection.Cell id={id} selection={selection} />}
                  <ListView.Cell>
                    <InfoContainer>
                      <OpportunityName>
                        <OpportunityLink opportunity={opportunity} onClick={(e) => e.stopPropagation()} />
                      </OpportunityName>
                      <small>{opportunity.company.name}</small>
                    </InfoContainer>
                  </ListView.Cell>
                  <ListView.Cell>
                    {owner && (
                      <Owner>
                        <MemberContactPopover member={owner} placement="left">
                          <Avatar value={owner} isCircle hasBackground initialsFontSize=".9rem" />
                        </MemberContactPopover>
                        <OwnerInfo>{owner.name}</OwnerInfo>
                      </Owner>
                    )}
                  </ListView.Cell>
                  <ListView.Cell>{opportunityPipeline.name}</ListView.Cell>
                  <ListView.Cell>
                    <StageContainer>
                      <span>{opportunityStage.name}</span>
                      <ProbabilityBar
                        probability={opportunityStage.probability}
                        stageStatus={opportunityStage.statusId}
                      />
                    </StageContainer>
                  </ListView.Cell>
                  <ListView.Cell>
                    <Tags>
                      <FirstProject projects={projects} />
                      <Projects projects={projects} />
                    </Tags>
                  </ListView.Cell>
                  <ListView.Cell>
                    <Tags>
                      <FirstAssignment tags={tags} />
                      <Assignments tags={tags} />
                    </Tags>
                  </ListView.Cell>
                  <ListView.Cell>
                    <p>
                      <Currency
                        value={convertedAmount}
                        currency={workspace.currency}
                        minimumFractionDigits={0}
                        maximumFractionDigits={2}
                      />
                      {currency !== workspace.currency && (
                        <Small>
                          <Currency
                            value={amount}
                            currency={currency}
                            minimumFractionDigits={0}
                            maximumFractionDigits={2}
                          />
                        </Small>
                      )}
                    </p>
                  </ListView.Cell>
                  <ListView.Cell>
                    <ListTooltip
                      message={
                        <List>
                          <List.Item label="Weighted Amount">
                            <MultiCurrency
                              value={[
                                { value: opportunity.convertedWeightedAmount, currency: workspace.currency },
                                { value: opportunity.weightedAmount, currency: opportunity.currency },
                              ]}
                            />
                          </List.Item>
                        </List>
                      }>
                      <Percent value={probability / 100} minimumFractionDigits={0} maximumFractionDigits={0} />
                    </ListTooltip>
                  </ListView.Cell>
                  <ListView.Cell>{practice?.name}</ListView.Cell>
                  <ListView.Cell>{{ high: 'High', medium: 'Medium', low: 'Low' }[priorityId]}</ListView.Cell>
                  <ListView.Cell>{opportunityType?.name}</ListView.Cell>
                  <ListView.Cell>{opportunityLeadSource?.name}</ListView.Cell>
                  <ListView.Cell>
                    <DateTime value={lastActivityDate} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <DateTime value={openDate} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <DateTime value={closeDate} />
                  </ListView.Cell>
                  {salesforceEnabled && <ListView.Cell>{salesforceId && <SalesforceIndicator />}</ListView.Cell>}
                  {hubspotEnabled && <ListView.Cell>{hubspotId && <HubspotIndicator />}</ListView.Cell>}
                  <ListViewActions isVisible={mode === 'page'}>
                    {auth.pipeline.manage ? (
                      <ListViewActions.Edit onClick={() => handleEdit(opportunity)} />
                    ) : (
                      <ListViewActions.View onClick={() => handleOverview(opportunity)} />
                    )}

                    <hr />

                    <ListViewMenu>
                      {({ setIsOpen }) => {
                        const handleAction = (action) => setIsOpen(false) || action;
                        const hasAssociatedProjects = projects?.length > 0;

                        return (
                          <>
                            <ListViewMenu.Item onClick={() => handleAction(handleOverview(opportunity))}>
                              View
                            </ListViewMenu.Item>

                            <ListViewMenu.Item
                              disabled={!permissions.manage}
                              tooltip={
                                !permissions.manage ? 'Insufficient permissions to edit this opportunity.' : undefined
                              }
                              onClick={() => handleAction(handleEdit(opportunity))}>
                              Edit
                            </ListViewMenu.Item>

                            <ListViewMenu.Item
                              disabled={hasAssociatedProjects || !permissions.manage}
                              tooltip={(() => {
                                if (!permissions.manage) return 'Insufficient permissions to delete this opportunity.';
                                if (hasAssociatedProjects)
                                  return 'This opportunity cannot be deleted because it is linked to one or more projects.';
                              })()}
                              onClick={() => handleAction(handleDeleteConfirmation(opportunity))}>
                              Delete
                            </ListViewMenu.Item>

                            <ListViewMenu.Item
                              disabled={!permissions.manage}
                              tooltip={
                                !permissions.manage ? 'Insufficient permissions to clone this opportunity.' : undefined
                              }
                              onClick={() => handleAction(setCloneTarget(opportunity))}>
                              Clone
                            </ListViewMenu.Item>

                            <ListViewMenu.Item
                              disabled={!permissions.manage}
                              tooltip={(() => {
                                if (!permissions.manage)
                                  return 'Insufficient permissions to create a project from this opportunity.';
                              })()}
                              onClick={() => handleAction(setOpportunityToProject(opportunity))}>
                              Create Project
                            </ListViewMenu.Item>
                          </>
                        );
                      }}
                    </ListViewMenu>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {query.data.results.length === 0 && <ListView.Empty />}

            {query.data.total > query.data.results.length && (
              <ListView.Loader key={query.data.results.length} onIntersecting={loadMore} />
            )}
          </ListView.Body>

          <ListView.Status total={query.data.total} label="Opportunity" isLoading={!!action} />
        </ListView>
      </Page.ListView>

      {mode === 'page' && selection.any && (
        <ActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <>
                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkSetStage();
                    setIsOpen(false);
                  }}>
                  Set Stage
                </BulkActionMenu.Item>
                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkSetPriority();
                    setIsOpen(false);
                  }}>
                  Set Priority
                </BulkActionMenu.Item>
                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkDelete();
                    setIsOpen(false);
                  }}>
                  Delete
                </BulkActionMenu.Item>
              </>
            )}
          </BulkActionMenu>
        </ActionsBanner>
      )}

      {opportunityId && (
        <OpportunityForm
          onSaved={(opportunity, isWon) => {
            if (isWon && opportunity.projects?.length === 0) {
              handleWon(opportunity);
            } else {
              handleSaved(opportunity);
            }
          }}
          onClose={() => handleClose()}
        />
      )}
      {cloneTarget && (
        <OpportunityCloneForm target={cloneTarget} onClose={() => setCloneTarget(null)} onSaved={handleCloned} />
      )}
      {opportunityToProject && (
        <OpportunityToProjectForm
          opportunity={opportunityToProject}
          onClose={() => setOpportunityToProject(false)}
          onSaved={(project) =>
            history.push(`/app/${workspace.key}/portfolio/projects/${project.client.key}/${project.key}/overview/edit`)
          }
        />
      )}

      {dialog}
    </Container>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);
  const features = useFeatures();
  const integrations = useIntegrations();

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  const salesforceEnabled = integrations?.salesforce;
  const hubspotEnabled = integrations?.hubspot;

  return (
    <FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      {salesforceEnabled && (
        <YesNoFilter
          icon="filter"
          clearable
          placeholder="Salesforce Synced"
          value={filters.opportunitySalesforceSynced}
          onChange={({ target: { value } }) => handleFilter({ opportunitySalesforceSynced: value })}
        />
      )}

      {hubspotEnabled && (
        <YesNoFilter
          icon="filter"
          clearable
          placeholder="HubSpot Synced"
          value={filters.opportunityHubspotSynced}
          onChange={({ target: { value } }) => handleFilter({ opportunityHubspotSynced: value })}
        />
      )}

      <FiltersGroup label="Company Filters" filters={[filters.company]}>
        <CompanyFilter
          name="company"
          placeholder="Company"
          materialAlwaysVisible
          value={filters.companies}
          onChange={({ target: { value } }) => handleFilter({ companies: value })}
        />
      </FiltersGroup>

      <FiltersGroup
        label="Opportunity Filters"
        filters={[
          filters.opportunityClosedPeriod,
          filters.opportunityStages,
          filters.opportunityStageCategories,
          filters.opportunityTags,
          filters.opportunityTypes,
          filters.opportunityOwners,
          filters.opportunityPractices,
          filters.opportunityAmountGreaterThanOrEqual,
        ]}>
        <ReportPeriodFilter
          value={filters.opportunityOpenedPeriod}
          placeholder="Open Date Range"
          onChange={({ target: { value } }) => handleFilter({ opportunityOpenedPeriod: value })}
        />

        <ReportPeriodFilter
          value={filters.opportunityClosedPeriod}
          placeholder="Expected Close Date Range"
          onChange={({ target: { value } }) => handleFilter({ opportunityClosedPeriod: value })}
        />

        <OpportunityPipelineFilter
          name="opportunityPipelines"
          placeholder="Opportunity Pipeline"
          value={filters.opportunityPipelines}
          onChange={({ target: { value } }) => handleFilter({ opportunityPipelines: value })}
        />

        <OpportunityStageCategoryFilter
          name="opportunityStageCategories"
          value={filters.opportunityStageCategories}
          onChange={({ target: { value } }) => handleFilter({ opportunityStageCategories: value })}
        />

        <OpportunityStageFilter
          name="opportunityStages"
          value={filters.opportunityStages}
          opportunityStageCategoryId={filters.opportunityStageCategories.map((status) => status.id)}
          opportunityPipelineId={filters.opportunityPipelines.map((pipeline) => pipeline.id)}
          onChange={({ target: { value } }) => handleFilter({ opportunityStages: value })}
        />

        <MemberFilter
          name="opportunityOwners"
          value={filters.opportunityOwners}
          onChange={({ target: { value } }) => handleFilter({ opportunityOwners: value })}
          placeholder="Opportunity Owner"
        />

        <OpportunityTagFilter
          name="opportunityTags"
          value={filters.opportunityTags}
          onChange={({ target: { value } }) => handleFilter({ opportunityTags: value })}
          placeholder="Opportunity Tags"
        />

        <OpportunityTypeFilter
          name="opportunityTypes"
          value={filters.opportunityTypes}
          onChange={({ target: { value } }) => handleFilter({ opportunityTypes: value })}
          placeholder="Opportunity Type"
        />

        <CurrencyInput
          name="opportunityAmountGreaterThanOrEqual"
          value={filters.opportunityAmountGreaterThanOrEqual}
          onChange={(value) => handleFilter({ opportunityAmountGreaterThanOrEqual: value })}
          placeholder="0"
          materialPlaceholder="Minimum Opportunity Amount"
          materialAlwaysVisible
        />

        {features.practices && (
          <PracticeFilter
            name="opportunityPractices"
            value={filters.opportunityPractices}
            onChange={({ target: { value } }) => handleFilter({ opportunityPractices: value })}
            placeholder="Opportunity Practice"
          />
        )}
      </FiltersGroup>
    </FiltersDrawer>
  );
}
